import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { avatarUrl } from "../lib/avatar";
import StrategyCard from "../components/StrategyCard";
import { Send } from "lucide-react";

export default function StrategyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [strategy, setStrategy] = useState(null);
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");

  const fetchStrategy = async () => {
    const { data } = await supabase
      .from("strategies")
      .select("*, author:profiles!user_id(id,name,avatar_url,participant_id), strategy_likes(user_id), comments(count)")
      .eq("id", id)
      .single();
    if (data) {
      setStrategy({
        ...data,
        like_count:  data.strategy_likes?.length || 0,
        liked_by_me: data.strategy_likes?.some((l) => l.user_id === user?.id) || false,
      });
    }
  };

  const fetchComments = async () => {
    const { data } = await supabase
      .from("comments")
      .select("*, author:profiles!user_id(id,name,avatar_url,participant_id)")
      .eq("strategy_id", id)
      .order("created_at", { ascending: true });
    setComments(data || []);
  };

  useEffect(() => {
    fetchStrategy();
    fetchComments();
  }, [id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLike = async (s) => {
    if (!user) return;
    const wasLiked = s.liked_by_me;
    setStrategy((prev) => ({ ...prev, liked_by_me: !wasLiked, like_count: wasLiked ? prev.like_count - 1 : prev.like_count + 1 }));
    if (wasLiked) {
      await supabase.from("strategy_likes").delete().eq("strategy_id", s.id).eq("user_id", user.id);
    } else {
      await supabase.from("strategy_likes").insert({ strategy_id: s.id, user_id: user.id });
    }
  };

  const onShare = async (s) => {
    if (!user) return;
    try {
      const { error } = await supabase.from("strategy_shares").insert({ strategy_id: s.id, user_id: user.id });
      if (!error) {
        setStrategy((prev) => ({ ...prev, shares: (prev.shares || 0) + 1 }));
      }
    } catch (_) {}
    try { await navigator.clipboard.writeText(window.location.href); } catch (_) {}
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    const { data } = await supabase
      .from("comments")
      .insert({ strategy_id: id, user_id: user.id, body: body.trim() })
      .select("*, author:profiles!user_id(id,name,avatar_url,participant_id)")
      .single();
    if (data) setComments((cs) => [...cs, data]);
    setBody("");
  };

  if (!strategy) return <div className="p-10 font-hand text-3xl">loading…</div>;

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <StrategyCard s={strategy} onLike={onLike} onShare={onShare} />

      <section className="brutal-card p-5 sm:p-6" data-testid="comments-section">
        <h3 className="font-display font-extrabold text-2xl uppercase tracking-tight mb-4">
          {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
        </h3>

        {user ? (
          <form onSubmit={submitComment} className="flex gap-2 mb-6" data-testid="add-comment-form">
            <input className="brutal-input flex-1" placeholder="Drop a thought…" value={body}
              onChange={(e) => setBody(e.target.value)} data-testid="comment-input" />
            <button type="submit" className="brutal-btn brutal-btn-yellow" data-testid="comment-submit-btn">
              <Send size={14} /> Post
            </button>
          </form>
        ) : (
          <Link to="/login" className="brutal-btn mb-6 inline-flex">Log in to comment</Link>
        )}

        <ul className="space-y-4">
          {comments.map((c) => (
            <li key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <Link to={`/u/${c.author?.id}`} className="shrink-0">
                <img src={avatarUrl(c.author?.avatar_url, c.author?.id)} alt={c.author?.name} className="w-10 h-10 border-2 border-ink blob bg-cream" />
              </Link>
              <div className="brutal-card-sm p-3 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-display font-extrabold uppercase tracking-tight">{c.author?.name}</span>
                  <span className="font-mono text-xs text-ink/60">{c.author?.participant_id}</span>
                </div>
                <div className="leading-relaxed">{c.body}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
